// Der Speicher im Browser (localStorage) mit Zuhören – für den
// Material-Entwurf, den zwei Fenster teilen („Material in eigenem Fenster“,
// PLAN.md, E3, „Handwerk“). Ändert ein Fenster den Entwurf, meldet der
// Browser das dem anderen (Ereignis „storage“); dasselbe Fenster erfährt es
// über die eigene Liste der Zuhörer. So zeigen beide immer denselben Stand.
//
// Dazu ein kleines Signal „bitte neu laden“: Gibt ein Fenster frei, ändert
// das Material oder setzt „gesehen“, lädt das andere die Besichtigung neu
// vom Server – sonst zeigte es noch den Stand von vorher. Seit S4 hört auch
// die Seitenleiste darauf (die Zahl „zu prüfen“, `useAmServerGeaendert`).

import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import { speicherLesen, speicherSchreiben, type Speicher } from "./material.ts";

function browserSpeicher(): Speicher | null {
  try {
    return window.localStorage;
  } catch {
    // Gesperrt (privater Modus, Einstellungen) – dann eben ohne Gedächtnis.
    return null;
  }
}

const zuhoerer = new Map<string, Set<() => void>>();

function melden(schluessel: string): void {
  for (const z of zuhoerer.get(schluessel) ?? []) z();
}

function abonnieren(schluessel: string, melde: () => void): () => void {
  let liste = zuhoerer.get(schluessel);
  if (!liste) {
    liste = new Set();
    zuhoerer.set(schluessel, liste);
  }
  liste.add(melde);
  // Aus einem anderen Fenster: der Schlüssel – oder null, wenn dort alles geleert wurde.
  const vonAussen = (e: StorageEvent) => {
    if (e.key === schluessel || e.key === null) melde();
  };
  window.addEventListener("storage", vonAussen);
  return () => {
    liste.delete(melde);
    window.removeEventListener("storage", vonAussen);
  };
}

export function gespeichertLesen(schluessel: string): string | null {
  return speicherLesen(browserSpeicher(), schluessel);
}

/** Schreibt (null: löscht) und sagt es allen in diesem Fenster; gibt zurück, ob es ging. */
export function gespeichertSchreiben(schluessel: string, wert: string | null): boolean {
  const ok = speicherSchreiben(browserSpeicher(), schluessel, wert);
  melden(schluessel);
  return ok;
}

/** Der gespeicherte Text unter diesem Schlüssel – aktuell, auch wenn ihn ein anderes Fenster ändert. */
export function useGespeichert(schluessel: string): string | null {
  const abo = useCallback((melde: () => void) => abonnieren(schluessel, melde), [schluessel]);
  return useSyncExternalStore(
    abo,
    () => gespeichertLesen(schluessel),
    () => null,
  );
}

// --- „Bitte neu laden“ an die anderen Fenster ------------------------------------------------

const SIGNAL_ANFANG = "he-crm-neu-";
const signalSchluessel = (aufnahmeId: string) => `${SIGNAL_ANFANG}${aufnahmeId}`;
/** Dasselbe Signal im eigenen Fenster – „storage“ kommt nur in den ANDEREN an. */
const IM_FENSTER = "he-crm-am-server-geaendert";

/**
 * Sagt den anderen Fenstern: Diese Besichtigung hat sich am Server geändert.
 * Seit S4 auch dem eigenen (für die Zahl „zu prüfen“ in der Leiste, die sonst
 * erst beim nächsten Seitenwechsel stimmte).
 */
export function anderenFensternMelden(aufnahmeId: string): void {
  speicherSchreiben(browserSpeicher(), signalSchluessel(aufnahmeId), String(Date.now()));
  window.dispatchEvent(new Event(IM_FENSTER));
}

/**
 * Ruft `geaendert`, sobald sich irgendeine Besichtigung am Server geändert
 * hat – freigegeben, Material geändert, „gesehen“ –, in diesem oder einem
 * anderen Fenster (seit S4: die Zahl „zu prüfen“ in der Seitenleiste).
 */
export function useAmServerGeaendert(geaendert: () => void): void {
  const aktuell = useRef(geaendert);
  useEffect(() => {
    aktuell.current = geaendert;
  });
  useEffect(() => {
    const hier = () => aktuell.current();
    const vonAussen = (e: StorageEvent) => {
      if (e.key?.startsWith(SIGNAL_ANFANG)) aktuell.current();
    };
    window.addEventListener(IM_FENSTER, hier);
    window.addEventListener("storage", vonAussen);
    return () => {
      window.removeEventListener(IM_FENSTER, hier);
      window.removeEventListener("storage", vonAussen);
    };
  }, []);
}

/** Lädt neu, wenn ein ANDERES Fenster meldet, dass sich die Besichtigung geändert hat. */
export function useNeuLadenSignal(aufnahmeId: string, neu: () => void): void {
  const neuAktuell = useRef(neu);
  useEffect(() => {
    neuAktuell.current = neu;
  });
  useEffect(() => {
    const schluessel = signalSchluessel(aufnahmeId);
    const vonAussen = (e: StorageEvent) => {
      if (e.key === schluessel) neuAktuell.current();
    };
    window.addEventListener("storage", vonAussen);
    return () => window.removeEventListener("storage", vonAussen);
  }, [aufnahmeId]);
}
